import { Outlet } from "react-router-dom";
import TopBar from "./TopBar";
import SideBar from "./SideBar";
import styled from "styled-components";
import { Suspense, useEffect, useState } from "react";
import { LinearProgress } from "@mui/material";
import MobileSidebar from "./MobileSidebar";
import CommandPalette from "./sidebar/CommandPalette";
import { SIDEBAR_COLLAPSED, SIDEBAR_EXPANDED } from "./SideMenu";

const DESKTOP_MIN = 768;
const COLLAPSED_KEY = "hipship-sidebar-collapsed";

const StyledMenu = styled.main<{ $offset: string }>`
  overflow-y: scroll;
  overflow-x: hidden;
  margin-top: 60px;
  height: calc(100vh - 60px);
  padding: 20px 24px 48px;
  @media (max-width: 767px) {
    padding: 14px 12px 40px;
  }
  background-image: linear-gradient(180deg, #ffffff, #e0e0e0);
  transition: margin-left 0.25s ease;
  margin-left: ${(props) => props.$offset};
`;

const readCollapsed = () => {
  try {
    const saved = localStorage.getItem(COLLAPSED_KEY);
    if (saved !== null) return saved === "1";
  } catch {}
  return true; // slim rail that opens on hover, unless the user chose to keep it open
};

// Typing in a field should never trigger the sidebar shortcut.
const isEditing = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));

const AppLayout = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= DESKTOP_MIN);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
      } catch {}
      return !c;
    });

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  // Ctrl/⌘+K opens page search anywhere; Ctrl/⌘+B collapses or expands the sidebar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const key = e.key.toLowerCase();
      if (key === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      } else if (key === "b" && isDesktop && !isEditing(e.target)) {
        e.preventDefault();
        toggleCollapsed();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop]);

  useEffect(() => {
    const handleRightClick = (e: Event) => {
      e.preventDefault();
    };

    document.body.addEventListener("contextmenu", handleRightClick);

    return () => {
      document.body.removeEventListener("contextmenu", handleRightClick);
    };
  }, []);

  const offset = isDesktop ? (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED) : "0px";

  return (
    <div>
      {isDesktop ? (
        <SideBar collapsed={collapsed} onToggle={toggleCollapsed} onSearch={() => setSearchOpen(true)} />
      ) : (
        <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} onSearch={() => setSearchOpen(true)} />
      )}

      <TopBar offset={offset} showMenuButton={!isDesktop} onMenuClick={() => setMobileOpen(true)} />
      <StyledMenu $offset={offset}>
        {/* Only the page area waits for a lazily loaded page; menu and top bar stay put. */}
        <Suspense fallback={<LinearProgress sx={{ height: 3, borderRadius: 2 }} />}>
          <Outlet />
        </Suspense>
      </StyledMenu>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default AppLayout;
